import CardData from '../dashboard/cardData';
import {
  ArrowRight,
  Briefcase,
  Calendar,
  CircleTick,
  DocumentAdd,
  DocumentText,
  DoubleChevronDown,
  DoubleChevronUp,
  Edit,
  FilledNote,
  NotificationBing,
  ShieldTick,
  TextBlock,
} from '../icons';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import {
  getLocalDateAndTime,
  getMatterIconClass,
  getMatterLabel,
} from '../../constants/utilityFunctions';
import { useState } from 'react';
import { ROUTES } from '../../constants/constants';
import {
  cardBorderColorMapping,
  GENERAL_ENTITY_DISABLED_ID,
  tagColorMapping,
  TasksPriority,
} from '../../constants/tasks';
import { ITransaction } from '../../interface/transaction';
import { useTransaction } from '../../context/TransactionContext';
import RenderTransactionRestrictions from '../dashboard/addTransaction/renderTransactionRestrictions';
import { ITask } from '../../interface/tasks';
import { useTask } from '../../context/TaskContext';
import { Button, CardCta, Divider, Paragraph } from '../Ant';
import { PrimaryCard, PriorityChip } from '../../assets/styles/components/dashboard.styled';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { Flex } from 'antd';
import DescriptionModal from '../descriptionModal/descriptionModal';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { HoursDurationDisplay } from '../hoursDurationDisplay/hoursDurationDisplay';
import NoteIcons from '../common/NoteIcons';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const GeneralCard = ({
  task,
  completeOpenTask,
  isLoading = false,
}: {
  task?: ITask;
  completeOpenTask: (task?: ITask, operation?: string) => void;
  isLoading?: boolean;
}) => {
  const { push } = useNavigationStack();
  const { t } = useTranslation();
  const { getAppLanguageCode } = useLanguage();
  const { updateTransaction } = useTransaction();
  const { updateTask, clearInitialTask } = useTask();
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState<boolean>(false);
  const [showMoreContent, setShowMoreContent] = useState<
    { title: string; content: string } | undefined
  >(undefined);

  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();

  const translatedStatus =
    task?.status?.translations?.find((t) => t.language === getAppLanguageCode())?.translation ??
    '-';

  const handleTaskToTransaction = (transaction?: ITransaction) => {
    if (transaction) {
      updateTransaction({
        matterId: task?.matterId,
        matter: task?.matter,
        subject: task?.subject,
        currentSubject: task?.subject,
        isManualUnitPrice: false,
        shouldLoadServerPrice: true,
        restrictPriceUpdate: true,
        principalCompany: task?.matter?.principalCompany,
        principalPrivatePerson: task?.matter?.principalPrivatePerson,
        overrideWarnings: true,
        activityId: task?.id,
        workedHours: task?.duration,
        billableQuantity: task?.duration,
      } as ITransaction);
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setSelectedTransaction(undefined);
    }
  };
  const handleEditTask = () => {
    if (task?.id) {
      updateTask(null);
      clearInitialTask();
      push(`${ROUTES.ADD_EDIT_TASK}/${task?.id}`);
    }
  };

  const handleClose = () => {
    setIsShowMoreModalOpen(false);
  };
  const handleGoToMatter = () => {
    push(ROUTES.MATTER_DETAIL + `/${task?.matter?.id}`);
  };
  if (isLoading || !task) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 2, 2, 1, 1, 1]}
        withSubcontentOnFirstRow
        className="px-10 py-10 h-full"
      />
    );
  }
  return (
    <>
      <PrimaryCard
        data-testid="general-card"
        $isDetail
        className={`primary-card task-card d-flex flex-col gap-10 h-full ${cardBorderColorMapping[task?.statusId as string] || cardBorderColorMapping.default} `}
      >
        <div className="d-flex gap-10 items-center w-full jc-space-between">
          <Flex gap={10} align="center">
            <div
              data-testid={`status-chip`}
              className={`status-chip d-flex justify-center items-center ${tagColorMapping[task?.statusId as string] || tagColorMapping.default}`}
            >
              {translatedStatus}
            </div>
            <NoteIcons hasImportantNotes={task?.hasImportantNotes} hasNotes={task?.hasNotes} />
          </Flex>

          {task?.priority === TasksPriority.high && (
            <PriorityChip data-testid={`priority-chip-high`}>
              <DoubleChevronUp className="opacity-50" />
              {t('task:priorityLevel.high')}
            </PriorityChip>
          )}
          {task?.priority === TasksPriority.low && (
            <PriorityChip data-testid={`priority-chip-low`}>
              <DoubleChevronDown className="opacity-50" />
              {t('task:priorityLevel.low')}
            </PriorityChip>
          )}
        </div>
        <Divider />
        <div className="d-flex flex-col gap-10 flex-grow w-full">
          <CardData
            type="task-subject"
            icon={<FilledNote />}
            title={t('task:task')}
            content={task?.subject ?? '-'}
            contentclass="lineclamp-1"
            moreAction={
              <Button
                type="text"
                data-testid={`show-more`}
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({ title: t('task:task'), content: task?.subject ?? '' });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />

          <CardData
            type="task-description"
            icon={<TextBlock />}
            title={t('task:description')}
            subcontent={task?.description?.trim() || '-'}
            subcontentclass="fw-400 lineclamp-2"
            moreAction={
              <Button
                type="text"
                data-testid={`show-more-description`}
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({
                    title: t('task:description'),
                    content: task?.description ?? '',
                  });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />

          <div className="d-flex justify-between">
            <CardData
              type="task-start-date"
              icon={<Calendar />}
              title={t('task:startDate')}
              content={getLocalDateAndTime(new Date(task?.startTime ?? ''), true)}
            />
            <CardData
              type="task-end-date"
              icon={<Calendar />}
              title={t('task:endDate')}
              content={getLocalDateAndTime(new Date(task?.dueTime ?? ''), true)}
            />
          </div>
          <div className="d-flex justify-between">
            <CardData
              type="task-alert-time"
              icon={<NotificationBing />}
              title={t('task:alertTime')}
              content={`${task?.alertTime ? getLocalDateAndTime(new Date(task?.alertTime)) : '-'}`}
            />
            <HoursDurationDisplay
              title={t('task:duration')}
              durationInHours={task?.duration ?? 0}
            />
          </div>
          <Divider />
          <CardData
            type="task-matter"
            icon={<Briefcase className={getMatterIconClass(task?.matter?.invoiceableType ?? '')} />}
            title={getMatterLabel(t, task?.matter?.invoiceableType ?? '')}
            content={
              task?.matter?.principalCompany?.name ??
              task?.matter?.principalPrivatePerson?.fullName ??
              ''
            }
            subcontent={(task?.matter?.matterNumber ?? '') + ' - ' + (task?.matter?.subject ?? '')}
            contentclass="lineclamp-1"
            subcontentclass="lineclamp-1"
            headingcontent={
              task?.matter && (
                <Button
                  data-testid={`view-matter`}
                  type="text"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={handleGoToMatter}
                >
                  {getViewLabel(t, 'matter:matter')}
                </Button>
              )
            }
          />

          <CardData
            type="transaction-subject"
            icon={<DocumentText />}
            title={t('transaction:transaction')}
            content={task?.transaction?.subject ?? '-'}
            contentclass="lineclamp-1"
          />
          <CardData
            type="responsible-person"
            icon={<ShieldTick />}
            title={t('task:responsiblePerson')}
            content={task?.responsibleUser?.fullName ?? '-'}
          />
        </div>
        <Flex vertical gap={10} className="w-full">
          <Divider />
          <Flex gap={10}>
            {!task?.transactionId && task?.statusId !== GENERAL_ENTITY_DISABLED_ID && (
              <CardCta
                data-testid={`create-transaction`}
                onClick={() => {
                  if (task?.matterId) {
                    setSelectedTransaction({
                      matter: task?.matter,
                      matterId: task?.matterId,
                    } as ITransaction);
                  } else {
                    handleTaskToTransaction({
                      matter: task?.matter,
                      matterId: task?.matterId,
                    } as ITransaction);
                  }
                }}
              >
                <DocumentAdd />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:taskToTransaction')}
                </Paragraph>
              </CardCta>
            )}
            {task?.statusId === GENERAL_ENTITY_DISABLED_ID ? (
              <CardCta
                data-testid={`mark-as-open`}
                onClick={() => {
                  completeOpenTask(task, 'open');
                }}
              >
                <CircleTick />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:openTask')}
                </Paragraph>
              </CardCta>
            ) : (
              <CardCta
                data-testid={`mark-as-complete`}
                onClick={() => {
                  completeOpenTask(task, 'complete');
                }}
              >
                <CircleTick />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:markAsComplete')}
                </Paragraph>
              </CardCta>
            )}
            {task?.statusId !== GENERAL_ENTITY_DISABLED_ID && (
              <CardCta data-testid={`edit-task`} onClick={handleEditTask}>
                <Edit />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:edit')}
                </Paragraph>
              </CardCta>
            )}
          </Flex>
        </Flex>
      </PrimaryCard>
      {/* Stays mounted through its closing animation, which is when antd hands focus back. */}
      <DescriptionModal
        title={showMoreContent?.title ?? ''}
        description={showMoreContent?.content ?? ''}
        open={isShowMoreModalOpen}
        onClose={handleClose}
      />
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleTaskToTransaction}
      />
    </>
  );
};

export default GeneralCard;
